import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { authApi } from '@/lib/api/endpoints';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('That does not look like an email address'),
});

export function ForgotPasswordRoute() {
  const [sent, setSent] = useState(false);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  // The API answers 202 whether or not the address exists, so the UI must not
  // reveal the difference either.
  const onSubmit = async ({ email }: z.infer<typeof schema>) => {
    await authApi.forgotPassword(email.trim()).catch(() => undefined);
    setSent(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      className="w-full max-w-[380px] space-y-7"
    >
      {sent ? (
        <div className="space-y-4">
          <span className="flex size-11 items-center justify-center rounded-xl bg-card shadow-card">
            <MailCheck className="size-5 text-moving" strokeWidth={1.8} />
          </span>
          <div className="space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">Check your inbox</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              If <span className="font-medium text-foreground">{form.getValues('email')}</span>{' '}
              belongs to an account, a reset link is on its way. It expires in one hour.
            </p>
          </div>
          <Button asChild variant="secondary" className="h-10 w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      ) : (
        <>
          <header className="space-y-1.5">
            <p className="label-micro">Account recovery</p>
            <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
            <p className="text-sm text-muted-foreground">
              We'll email you a link to choose a new one.
            </p>
          </header>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-micro">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        autoFocus
                        placeholder="you@fleet.com"
                        className="h-10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={form.formState.isSubmitting} className="h-10 w-full">
                {form.formState.isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  'Send reset link'
                )}
              </Button>
            </form>
          </Form>

          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" /> Back to sign in
          </Link>
        </>
      )}
    </motion.div>
  );
}
